import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Stack } from '@mui/material'
import {
  Box,
  Button,
  ClickAwayListener,
  FormControlLabel,
  Grow,
  List,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseIcon from '@mui/icons-material/Close'
import { head, isEmpty, isEqual } from 'lodash'
import { Trans, useTranslation } from 'react-i18next'
import { KeyLabelType, StatusSelectorType, TransactionsMethodsList } from '../../../types'
import { MultiSelectFiltersModal } from '../MultiSelectFiltersModal'
import { StyledCheckbox } from '../StyledComponents'
import {
  StyledBoxSelector,
  StyledCardLabel,
  StyledIconButton,
  StyledISelectorIconButton,
  StyledItem,
  StyledSelectorPaper,
  StyledPopper,
} from './StyledComponents'

const MultipleFilterSelectorComponent: FC<{
  optionsList: KeyLabelType[]
  setValue: (value: Array<string | number>) => void
  currentValue: Array<string | number>
  filterLabel?: string
  defaultOptionLabel: string
  name: string
  doubleRows?: boolean
}> = ({
  optionsList,
  setValue,
  currentValue,
  filterLabel,
  defaultOptionLabel,
  name,
  doubleRows,
}) => {
  const { t } = useTranslation()
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [tempValuesList, setTempValuesList] = useState<Array<string | number>>(currentValue)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const currentLabel = useMemo(() => {
    if (!currentValue.length || currentValue.length === optionsList.length) {
      return defaultOptionLabel
    }
    if (currentValue.length > 1) return t('multipleOptions', 'Multiple options')
    return optionsList.find(({ key }) => key === head(currentValue))?.label || '-'
  }, [currentValue, optionsList, defaultOptionLabel])

  const handleSelect = useCallback(
    (optionKey: string | number) => () => {
      const isChecked = tempValuesList.includes(optionKey)
      const newValue = !isChecked
        ? [...tempValuesList, optionKey]
        : tempValuesList.filter((item) => item !== optionKey)

      setTempValuesList(newValue.length === optionsList.length ? [] : newValue)
    },
    [optionsList, tempValuesList],
  )

  const handleToggle = () => {
    setOpen((prevOpen) => {
      if (!prevOpen) {
        setTempValuesList(currentValue)
      }
      return !prevOpen
    })
  }

  const applyOptions = () => {
    setOpen(false)
    setValue(tempValuesList)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const resetList = useCallback(() => {
    setTempValuesList([])
    setValue([])
    setOpen(false)
  }, [setValue])

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setTempValuesList([])
    !isEmpty(currentValue) && setValue([])
  }

  return (
    <>
      <StyledBoxSelector data-test="filter">
        <StyledIconButton
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          sx={(theme) => ({
            width: '100%',
            borderBottom: '1px solid #c4c4c4',
            '& .MuiSvgIcon-root': {
              position: 'absolute',
              right: 5,
            },
            '&.open': {
              borderBottom: '1px solid #000000',
              backgroundColor: 'transparent',
              '& .MuiSvgIcon-root': {
                color: '#000000',
                transform: 'rotate(180deg)',
              },
            },
            [theme.breakpoints.down('xs')]: {
              margin: 0,
            },
          })}
          className={open ? 'open' : ''}
          data-test="autotest-filterBtn"
          disableRipple
        >
          <Stack flexDirection="row" width="100%" alignItems="center">
            <Typography
              variant="subtitle2"
              sx={{
                position: 'absolute',
                top: 0,
                left: theme.spacing(1.5),
                color: '#999999',
              }}
            >
              {filterLabel || name}
            </Typography>
            <StyledCardLabel>{t(`${currentLabel}`)}</StyledCardLabel>

            {!isEmpty(currentValue) && (
              <StyledISelectorIconButton onClick={handleClear} aria-label="clear" size="medium">
                <CloseIcon fontSize="small" />
              </StyledISelectorIconButton>
            )}
          </Stack>

          {open ? (
            <ExpandMoreIcon />
          ) : (
            <ExpandMoreIcon
              style={{
                color: '#999999',
              }}
            />
          )}
        </StyledIconButton>

        <StyledPopper
          open={open && !isSmallScreen}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          placement="bottom-start"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: placement !== 'bottom-start' ? 'bottom' : 'top' }}
            >
              <StyledSelectorPaper>
                <ClickAwayListener onClickAway={handleClose}>
                  <Box>
                    <List
                      id="menu-list-grow"
                      sx={{
                        boxShadow: '5px 5px 4px 0px rgba(34, 60, 80, 0.2)',
                        backgroundColor: 'white',
                        '& :first-letter': {
                          textTransform: 'capitalize',
                        },
                      }}
                    >
                      <StyledItem
                        sx={{
                          paddingRight: theme.spacing(1),
                          paddingLeft: theme.spacing(6),
                          '& .MuiFormControlLabel-root': {
                            gap: theme.spacing(1),
                          },
                        }}
                        onClick={resetList}
                      >
                        <ListItemText>{defaultOptionLabel}</ListItemText>
                      </StyledItem>
                      {!isEmpty(optionsList) &&
                        (optionsList as TransactionsMethodsList[])?.map(({ key, label }) => {
                          const isOptionDisabled =
                            (name === 'status' &&
                              key === StatusSelectorType.Received &&
                              !!tempValuesList.find((item) => item === StatusSelectorType.Sent)) ||
                            (key === StatusSelectorType.Sent &&
                              !!tempValuesList.find((item) => item === StatusSelectorType.Received))
                          return (
                            <StyledItem
                              key={key}
                              component="button"
                              sx={{
                                border: 'none',
                                paddingRight: theme.spacing(1),
                                '& .MuiFormControlLabel-root': {
                                  gap: theme.spacing(1),
                                },
                              }}
                              data-test={`autotest-${key}`}
                              disabled={isOptionDisabled}
                            >
                              <FormControlLabel
                                control={
                                  <StyledCheckbox
                                    onChange={handleSelect(key)}
                                    checked={tempValuesList.includes(key)}
                                    disabled={isOptionDisabled}
                                  />
                                }
                                label={
                                  <Trans i18nKey={label as string} defaults={label as string} />
                                }
                              />
                            </StyledItem>
                          )
                        })}
                    </List>
                    <Box
                      sx={{
                        position: 'sticky',
                        bottom: 0,
                        background: '#fff',
                        padding: theme.spacing(0, 1, 1, 1),
                      }}
                    >
                      <Button
                        sx={{ padding: 0 }}
                        variant="outlined"
                        fullWidth
                        onClick={applyOptions}
                        size="small"
                      >
                        <ListItemText>{t('submit', 'Submit')}</ListItemText>
                      </Button>
                    </Box>
                  </Box>
                </ClickAwayListener>
              </StyledSelectorPaper>
            </Grow>
          )}
        </StyledPopper>
      </StyledBoxSelector>

      <MultiSelectFiltersModal
        open={isSmallScreen && open}
        onClose={applyOptions}
        onSelect={handleSelect}
        textHeader={name}
        options={optionsList}
        currentValue={tempValuesList}
        resetList={resetList}
        doubleRows={doubleRows}
      />
    </>
  )
}

export const MultipleFilterSelector = React.memo(MultipleFilterSelectorComponent, isEqual)
